// Forecast-log status badge, shared by the row variants in forecastLog.jsx
// (WatchlistRow, RecentRow). Pure so it's unit-testable in isolation.
//
// Only `showBadge`/`label` are read by those rows; the colour/token it also
// returns lost its last consumer when the legacy row was retired.
//
// A row reads RUNNING when its own forecast is running OR any of its branches
// is still generating. The legacy forecast projection emits a derived
// `_branchRunning` boolean (selectors.js `_runToLegacyForecast`), true when any
// descendant run is PREPROCESSING/RUNNING. So a completed root with a live
// branch still badges active in the log — matching the intent "RUNNING whenever
// there's an active branch/forecast ongoing", not just the root's own status.
// Otherwise it falls back to the row's literal status (errored / cancelled);
// 'completed' (and legacy backfilled rows) show no badge.
//
// Colors are hardcoded, not pulled from the theme palette — status is semantic,
// and the Dawn theme re-maps the yellow token to red, which would otherwise
// make RUNNING and ERRORED look identical.

/**
 * @param {{status?: string, _branchRunning?: boolean, contractStatus?: string, resolution?: {status?: string}}} f - a
 *   forecast row (or the active forecast).
 * @returns {{showBadge: boolean, color: string, textColor: string, label: string, token?: string}}
 *   `token`, when present, names a THEME palette key (e.g. "blueTeam"/"redTeam")
 *   the row component resolves to a hex so the badge tracks the active color
 *   scheme; `color` is the static fallback for consumers that don't map it.
 */
export function forecastRowBadge(f) {
  // A RESOLVED contract shows its OUTCOME in the badge slot instead of a
  // lifecycle status: a watchlisted forecast that has resolved stays on the list
  // (see WatchlistLogContent) reading OCCURRED / DID NOT OCCUR rather than
  // silently dropping off. contractStatus (the data-plane policy status) marks
  // "resolved"; resolution.status picks the verdict. The token names the same
  // yes/no colors the timeline triggers use (blueTeam = occurred, redTeam = did
  // not occur), so the pill matches the trigger palette across themes. This
  // takes precedence over the lifecycle status below (a resolved contract is
  // terminal — its own run status is 'completed', which would show no badge).
  if (f?.contractStatus === "resolved") {
    const rs = f?.resolution?.status;
    // `color` mirrors the token's base palette value (lib/colors.js
    // blueTeam/redTeam) so a consumer that reads `color` without mapping the
    // token still gets the token's default hue, not a divergent one.
    if (rs === "resolved_yes")
      return {
        showBadge: true,
        token: "blueTeam",
        color: "#4488ff",
        textColor: "#fff",
        label: "OCCURRED",
      };
    if (rs === "resolved_no")
      return {
        showBadge: true,
        token: "redTeam",
        color: "#ff4455",
        textColor: "#fff",
        label: "DID NOT OCCUR",
      };
    // resolved_tie, or a resolved contract with no conclusive verdict yet:
    // a neutral "RESOLVED" pill (no occurred/not claim), no theme token.
    return { showBadge: true, color: "#8899aa", textColor: "#fff", label: "RESOLVED" };
  }
  const branchRunning = !!f?._branchRunning;
  const effective = branchRunning ? "running" : f?.status;
  return {
    showBadge: !!effective && effective !== "completed",
    color:
      effective === "running"
        ? "#F7CE46"
        : effective === "preprocessing"
          ? "#5B8DEF"
          : effective === "errored"
            ? "#c0392b"
            : "#8899aa",
    textColor: effective === "running" ? "#111" : "#fff",
    label:
      effective === "running"
        ? "RUNNING"
        : effective === "preprocessing"
          ? "PREPARING"
          : effective === "errored"
            ? "ERRORED"
            : effective === "cancelled"
              ? "CANCELLED"
              : effective,
  };
}
